// src/features/payment/hooks/useOrderProcessing.ts
import { useEffect, useState } from "react";
import { useCartData } from "src/business-types/laundry/modules/home/Payment/hooks/useCartData";
import { useCustomerData } from "src/business-types/laundry/modules/home/Payment/hooks/useCustomerData";
import { useCurrentTime } from "src/business-types/laundry/modules/home/Payment/hooks/useCurrentTime";
import { usePayment } from "src/business-types/laundry/modules/home/Payment/hooks/usePayment";
import { errorHandling } from "src/shared/utils/errorHandling";
import { deleteShoppingCart, postOrder } from "src/shared/services/servicesv2";
import { processCardPayment } from "src/paymentGatway/useCardPayment";
import { useUser } from "src/shared/context/UserContext";

export const useOrderProcessing = (cartId?: string | null, editedAmounts: { [productId: string]: string } = {}) => {
  const [backToPayment, setBackToPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [showBalance, setShowBalance] = useState(false);
  const [prvDelete, setPrvDelete] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);
  const { cartProducts, loading: cartLoading } = useCartData(cartId);
  const customerId = cartProducts?.[0]?.customerId;
  const { customer } = useCustomerData(customerId);
  const { currentTime } = useCurrentTime();
  const { roundAmount } = usePayment();
  const { userConfigration } = useUser();

  const updatedProducts =
    cartProducts[0]?.products?.map((product) => {
      const editedPrice = Number(editedAmounts[product.productId] ?? product.unitPrice);

      return {
        ...product,
        unitPrice: editedPrice,
        itemTotal: editedPrice * Number(product.quantity),
      };
    }) || [];

  useEffect(() => {
    const balance = customer?.totalOutstandingBalance;

    if (balance != null && balance <= 0) {
      setShowBalance(true);
    } else {
      setShowBalance(false);
    }
  }, [customer?.totalOutstandingBalance]);

  useEffect(() => {
    setReceivedAmount(0);
  }, [showBalance]);

  const now = new Date();

  const generateOrderId = () => {
    return `${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}${String(now.getHours()).padStart(2, "0")}${String(
      now.getMinutes(),
    ).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
  };

  const handleOrderSuccess = async (method: "Cash" | "Buy on Credit" | "Card") => {
    setButtonLoading(true);
    const orderAmount = updatedProducts.reduce((sum, product) => sum + Number(product.itemTotal), 0);
    const discount = cartProducts.reduce((sum, item) => sum + parseFloat(item.discount || "0"), 0);
    const deliveryFee = cartProducts.length > 0 ? Number(cartProducts[0].deliveryFee || 0) : 0;

    const finalAmount = orderAmount - discount + deliveryFee;

    const localDate = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");
    const newOrderId = generateOrderId();
    setOrderId(newOrderId);

    const out_standing_balance = Number(customer?.totalOutstandingBalance)
    const finalCheck = out_standing_balance < 0 ? finalAmount >= Math.abs(out_standing_balance) ? Math.abs(out_standing_balance) : finalAmount : 0
    const received_amount = out_standing_balance < 0 ? ((receivedAmount ?? 0) + finalCheck) : receivedAmount
    const adjust = out_standing_balance < 0 ? finalCheck : 0

    const order = {
      customerId: cartProducts[0]?.customerId,
      products: updatedProducts,
      orderId: newOrderId,
      orderStatus: "Confirm",
      orderDate: localDate,
      orderTime: now.toTimeString().split(" ")[0],
      orderAmount: orderAmount.toString(),
      discount: discount.toString(),
      finalAmount: finalAmount.toString(),
      outstandingBalance: customer?.totalOutstandingBalance ?? 0,
      cancelOrderAmount: "0",
      deliveryFee: Number(deliveryFee),
      payments: { paymentMethod: method, receivedAmount: method !== "Buy on Credit" ? receivedAmount.toString() : "0", creditAdjust: adjust, },
      paymentHistory: [{ paymentMethod: method, receivedAmount: method !== "Buy on Credit" ? receivedAmount.toString() : "0" }],
      remainingAmount: Math.max(finalAmount - received_amount, 0).toString()
    };

    if (method === "Card" && Boolean(userConfigration?.cardPayment) === true) {
      await processCardPayment({
        amount: Math.round(Number(receivedAmount)),
        description: `Order payment for ${cartProducts[0]?.customerId || "Unknown Customer"}`,
        onSuccess: async (paymentDetails) => {
          const paysucess = {
            ...order,
            paymentDetails,
          };
          try {
            const response = await postOrder(paysucess);
            const orderId = response.data.Id;
            if (orderId && cartId) {
              // await api.delete(`/app/pos/v2/shopping_cart/${cartId}`);
              await deleteShoppingCart(cartId);
              setPrvDelete(orderId);
              setPaymentSuccess(true);
            }
          } catch (error) {
            console.error("Error saving order:", error);
            errorHandling(error);
            setButtonLoading(false);
          }

        },
      });
      return;
    }

    try {
      const response = await postOrder(order);
      const orderId = response.data.Id;
      if (orderId && cartId) {
        await deleteShoppingCart(cartId);
        setPrvDelete(orderId);
        setPaymentSuccess(true);
      }
    } catch (error) {
      console.error("Error saving order:", error);
      errorHandling(error);
      setButtonLoading(false);
    }
  };

  return {
    cartProducts,
    customer,
    cartLoading,
    // customerLoading,
    backToPayment,
    setBackToPayment,
    paymentSuccess,
    orderId,
    receivedAmount,
    setReceivedAmount,
    showBalance,
    setShowBalance,
    roundAmount,
    currentTime,
    handleOrderSuccess,
    prvDelete,
    buttonLoading,
  };
};
